import { isObject, isArray, isUndefined, isDate } from "lodash";
import config from "@client.config";
import Pages from "@client.enums/pages";

export default class BaseService {
  controllerUrl;

  constructor(controllerUrl) {
    this.controllerUrl = controllerUrl;
  }

  async fetchData(endPoint, options = {}, isExternalService) {
    const optionalSettings = {};
    if (!isExternalService) {
      optionalSettings["credentials"] = "include";
    }
    const requestOptions = {
      headers: {
        ...options.headers,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      method: options.method || "GET",
      body: options.body,
      ...optionalSettings,
    };
    let urlParts;
    if (isExternalService) {
      urlParts = [endPoint];
    } else {
      urlParts = this.controllerUrl
        ? [config.apiUrl, this.controllerUrl, endPoint]
        : [config.apiUrl, endPoint];
    }
    return fetch(
      urlParts.filter((v) => v.length > 0).join("/"),
      requestOptions
    );
  }

  async api(endPoint, options = {}, isExternalService, isFileDownload) {
    const { headers, ...rest } = options;
    let url = endPoint;
    if (!rest.method && Object.keys(rest).length > 0) {
      url = `${endPoint}?${this.prepareRequestQuery("", rest, true)}`;
    }
    return this.fetchData(
      url,
      rest.method ? options : { headers: headers },
      isExternalService
    )
      .then(async (response) => {
        if ([403, 401].includes(response.status)) {
          const publicPages = [Pages.Login, Pages.Demo];
          const isPublicPage = publicPages.some(
            (page) => window.location.pathname.indexOf(page) !== -1
          );
          if (!isPublicPage) {
            window.location.replace(Pages.Login);
            return Promise.resolve(null);
          }

          const res = await response.json();
          if (res.error) {
            throw new Error(res.error);
          }
        }

        if (response.ok) {
          if (response.status === 204) {
            return Promise.resolve(null);
          }

          if (isFileDownload) {
            await BaseService.processFileDownload(response);
            return Promise.resolve(null);
          } else {
            const res = await response.json();

            if (response.status !== 200 && res.error) {
              throw new Error(res.error);
            }
            return res;
          }
        }

        const res = await response.json();
        if (response.status !== 200 && res.error) {
          throw new Error(
            JSON.stringify(
              isArray(res.error) ? res.error : [{ msg: res.error }]
            )
          );
        }

        return res;
      })
      .catch((err) => {
        throw new Error(err.message);
      });
  }

  static async processFileDownload(response) {
    const contentDisposition = response.headers.get("Content-Disposition");
    const filenameMatch =
      contentDisposition && contentDisposition.match(/filename="(.+)"/);

    const filename = filenameMatch ? filenameMatch[1] : "output.csv";

    const blob = await response.blob();
    const link = document.createElement("a");

    link.href = window.URL.createObjectURL(blob);
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  static getRequestOptions(httpMethod, data = null) {
    const options = {
      method: httpMethod,
    };
    if (data) {
      options.body = JSON.stringify(data);
    }

    return options;
  }

  async post(endPoint, data = null, isExternalService = false) {
    return this.api(
      endPoint,
      BaseService.getRequestOptions("POST", data),
      isExternalService
    );
  }

  async delete(endPoint, data = null, isExternalService = false) {
    return this.api(
      endPoint,
      BaseService.getRequestOptions("DELETE", data),
      isExternalService
    );
  }

  async put(endPoint, data = null, isExternalService = false) {
    return this.api(
      endPoint,
      BaseService.getRequestOptions("PUT", data),
      isExternalService
    );
  }

  prepareRequestQuery = (prefix, val, top) => {
    if (isUndefined(top)) top = true;
    if (isArray(val)) {
      return val
        .map((value, key) => {
          return this.prepareRequestQuery(
            top ? key : `${prefix}[]`,
            value,
            false
          );
        })
        .join("&");
    } else if (isDate(val)) {
      return `${encodeURIComponent(prefix)}=${val.toISOString()}`;
    } else if (isObject(val)) {
      return Object.keys(val)
        .map((key) => {
          return this.prepareRequestQuery(
            top ? key : `${prefix}[${key}]`,
            val[key],
            false
          );
        })
        .join("&");
    } else {
      return `${encodeURIComponent(prefix)}=${encodeURIComponent(val)}`;
    }
  };
}
